import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { cn } from "@/core/helpers/cn";
import useClickAway from "@/core/helpers/useClickAway";
import { Button, Icon } from "@/shared/ui";
import { FactionEnum } from "@/domains/faction/constants/factionEnum";

type Props = {
  title: string;
  description: string;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  isConfirming?: boolean;
  faction: FactionEnum;
};

export default function FactionConfirmModal({
  title,
  description,
  onClose,
  onConfirm,
  isLoading,
  isConfirming,
  faction,
}: Props) {
  const { t } = useTranslation("faction");

  const ref = useClickAway(onClose);

  let gradient = "oliveShadow";
  if (faction === FactionEnum.HOSPITAL) {
    gradient = "redShadow";
  }
  if (faction === FactionEnum.MEDSI || faction === FactionEnum.REGION24) {
    gradient = "lightBlueShadow";
  }

  return (
    <div
      className={clsx(
        "absolute z-10 top-0 left-0 right-0 bottom-0 bg-[#0F0F0F]/95",
        "flex items-center justify-center",
        "animate-[--animation-fade-in]",
      )}
    >
      {isLoading && (
        <div className="flex items-center justify-center p-4">
          <div className="mz-loader" />
        </div>
      )}

      {!isLoading && (
        <div
          ref={ref}
          className={cn(
            "w-[25.125rem] p-5 bg-gradient-to-b from-[#0F0F0F] to-[#0F0F0F]/90 rounded-[0.875rem]",
            "relative p-7.5 flex flex-col items-center",
            gradient,
          )}
        >
          <Button
            variant="surface"
            color="gray"
            className="absolute top-2.5 right-2.5 size-7.5 p-0"
            onClick={onClose}
          >
            <Icon name="close" className="w-3" />
          </Button>
          <div className="text-18 font-bold text-white">{title}</div>
          <div className="h-px w-[20rem] bg-[#1F1F1F]/80 flex justify-center my-5">
            <div className="h-full w-[5rem] bg-accent-solid" />
          </div>
          <p className="mb-5 text-16 font-medium text-center">{description}</p>
          <div className="grid grid-cols-2 gap-2 w-full">
            <Button
              className="gap-2.5 h-11"
              onClick={onConfirm}
              loading={isConfirming}
              disabled={isConfirming}
            >
              {t("faction.yes")}
            </Button>
            <Button
              variant="solid-surface"
              className="h-11"
              onClick={onClose}
              loading={isConfirming}
              disabled={isConfirming}
            >
              {t("faction.cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
